import { agreementIdIn, AgreementKindEnum, filterValueFor } from '../agreement';
import type { Agreements } from '../agreement';
import { SearchParameters } from '../searchParameters';
import { type Agreements as AgreementSelection } from '../searchParameters';
import { type SearchPreferences } from '../searchPreferences';

// Anything that is not a positive integer reads as "never chose", so an absent
// preference cannot collide with a real agreement id.
const storedPaAgreementId = (
  preferences: SearchPreferences
): number | undefined => {
  const id = Number(preferences.paAgreement);

  return Number.isInteger(id) && id > 0 ? id : undefined;
};

// The visitor's stored choice while it still works, else the first with
// allowance left.
const defaultPremiumAccess = (
  agreements: Agreements,
  preferred: number | undefined
): string | undefined => {
  const stored = agreements.paAgreements.find((pa) => pa.id === preferred);
  const fallback = stored?.isExhausted()
    ? agreements.availablePaAgreements()[0]
    : stored ?? agreements.availablePaAgreements()[0];

  return fallback && filterValueFor(AgreementKindEnum.PA, fallback.id);
};

/**
 * PA is the only kind that can be substituted rather than merely dropped, and
 * the branch matters: with several usable agreements there is no way to know
 * which was meant, so an unrecognised selection is dropped instead of replaced.
 *
 * A kind with no id needs no guard here. The deserializer drops any token
 * without one, so a selection cannot carry it.
 */
const reconcilePremiumAccess = (
  selected: string,
  agreements: Agreements,
  preferred: number | undefined
): string | undefined => {
  if (agreements.paAgreements.length === 0) return undefined;

  const requested = agreements.paAgreements.find(
    (pa) => pa.id === agreementIdIn(selected)
  );

  if (requested) {
    return requested.isExhausted()
      ? defaultPremiumAccess(agreements, preferred)
      : selected;
  }

  return agreements.availablePaAgreements().length > 1
    ? undefined
    : defaultPremiumAccess(agreements, preferred);
};

const ownedBy = (
  agreements: Agreements,
  selected: string | undefined
): string | undefined =>
  selected && agreements.owns(selected) ? selected : undefined;

// Ownership is necessary but not sufficient for RF: an owned agreement with no
// allowance left cannot scope a search either.
const usableRoyaltyFree = (
  agreements: Agreements,
  selected: string | undefined
): string | undefined =>
  ownedBy(agreements, selected) && !agreements.rfAgreement?.isExhausted()
    ? selected
    : undefined;

const entryFor = (
  name: keyof AgreementSelection,
  value: string | undefined
): AgreementSelection => (value ? { [name]: value } : {});

const unchanged = (
  before: AgreementSelection,
  after: AgreementSelection
): boolean =>
  before.premiumaccess === after.premiumaccess &&
  before.easyaccess === after.easyaccess &&
  before.editorialsubscription === after.editorialsubscription &&
  before.royaltyfreesubscription === after.royaltyfreesubscription;

/**
 * Reconciles what the search asks for against what the visitor holds: an
 * exhausted agreement must not scope a search, and one they do not own must
 * not be honoured at all.
 *
 * Carries no sentinel for "deliberately cleared". A stateless server needed
 * one to tell that from "never touched"; here they are `undefined` and `{}`,
 * so the distinction needs no encoding.
 *
 * There is no blanket branch for an anonymous visitor or for holding none
 * either: the per-slot rules below already produce an empty selection in both
 * cases, and a second statement of a rule can only drift from the first.
 *
 * Returns the search itself when nothing changed: `with()` allocates even for
 * an empty change set, so identity is the only signal a caller has.
 */
export const reconcileAgreements = (
  searchParameters: SearchParameters,
  agreements: Agreements,
  preferences: SearchPreferences
): SearchParameters => {
  const selection = searchParameters.agreements;
  if (selection === undefined) return searchParameters;

  const reconciled: AgreementSelection = {
    ...entryFor(
      'premiumaccess',
      selection.premiumaccess &&
        reconcilePremiumAccess(
          selection.premiumaccess,
          agreements,
          storedPaAgreementId(preferences)
        )
    ),
    ...entryFor('easyaccess', ownedBy(agreements, selection.easyaccess)),
    ...entryFor(
      'editorialsubscription',
      ownedBy(agreements, selection.editorialsubscription)
    ),
    ...entryFor(
      'royaltyfreesubscription',
      usableRoyaltyFree(agreements, selection.royaltyfreesubscription)
    ),
  };

  return unchanged(selection, reconciled)
    ? searchParameters
    : searchParameters.with({ agreements: reconciled });
};
